import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Breadcrumb, Icon, Table, Button, Message, Segment, Label, Input, Modal } from 'semantic-ui-react';
import _ from 'lodash';
import Page from '../../../components/Page';
import MyMenu from '../menu';
import CreateDialog360Template from '../../../components/CreateDialog360Template';
import { post } from '../../../util/api';

function useQuery() {
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	return { waba_gen_id: params.get('waba_gen_id') || '', shop: params.get('shop') || '' };
}

function statusColor(status) {
	const s = (status || '').toLowerCase();
	if (s === 'approved') return 'green';
	if (s === 'rejected') return 'red';
	if (s === 'pending') return 'yellow';
	return 'grey';
}

export default function Dialog360Templates() {
	const { t } = useTranslation();
	const { waba_gen_id, shop } = useQuery();
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [search, setSearch] = useState('');
	const [modalOpen, setModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [viewTarget, setViewTarget] = useState(null);

	const fetchTemplates = useCallback(async () => {
		if (!waba_gen_id) {
			setError(t('wabaIdRequired') || 'waba_gen_id bulunamadı');
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const resp = await post('/dahi/channel/dialog360template/list', { waba_gen_id });
			const body = resp && resp.data;
			if (body && body.success) {
				const result = body.result || {};
				const list = result.waba_templates || result.data || (Array.isArray(result) ? result : []);
				setTemplates(list || []);
				setError('');
			} else {
				setError((body && body.message) || (t('errorFetchingTemplates') || 'Şablonlar alınamadı'));
			}
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, [waba_gen_id, t]);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	async function confirmDelete() {
		setDeleting(true);
		try {
			const resp = await post('/dahi/channel/dialog360template/delete', { waba_gen_id, name: deleteTarget.name });
			if (resp && resp.data && resp.data.success) {
				await fetchTemplates();
			} else {
				setError((resp && resp.data && resp.data.message) || (t('errorDeletingTemplate') || 'Şablon silinemedi'));
			}
		} catch (e) {
			setError(e.message);
		} finally {
			setDeleting(false);
			setDeleteTarget(null);
		}
	}

	const filtered = templates.filter((tpl) => {
		const q = search.toLowerCase();
		return !q || (tpl.name || '').toLowerCase().includes(q) || (tpl.category || '').toLowerCase().includes(q);
	});

	function bodyText(tpl) {
		const comp = _.find(tpl.components, (c) => (c.type || '').toLowerCase() === 'body');
		return (comp && comp.text) || '';
	}
	function carouselKind(tpl) {
		const car = _.find(tpl.components, (c) => (c.type || '').toLowerCase() === 'carousel');
		if (!car) return null;
		const firstCard = car.cards && car.cards[0];
		const header = firstCard && _.find(firstCard.components, (c) => (c.type || '').toLowerCase() === 'header');
		const fmt = header && (header.format || '').toUpperCase();
		return fmt === 'PRODUCT' ? 'product' : 'media';
	}
	function templateTypeLabel(tpl) {
		const k = carouselKind(tpl);
		if (k === 'product') return t('productCarousel') || 'Ürün Carousel';
		if (k === 'media') return t('mediaCarousel') || 'Medya Carousel';
		return t('standardTemplate') || 'Standart';
	}
	function mediaUrl(h) {
		return h && h.example && h.example.header_handle && h.example.header_handle[0];
	}
	function renderTemplateDetail(tpl) {
		if (!tpl) return null;
		const comps = tpl.components || [];
		const get = (type) => _.find(comps, (c) => (c.type || '').toLowerCase() === type);
		const header = get('header');
		const body = get('body');
		const footer = get('footer');
		const buttons = get('buttons');
		const carousel = get('carousel');
		return (
			<div>
				<div style={{ marginBottom: 10 }}>
					<Label>{tpl.category}</Label> <Label>{tpl.language}</Label> <Label color={statusColor(tpl.status)}>{tpl.status}</Label>
				</div>
				{header && (
					<Segment>
						<Label ribbon>HEADER · {header.format}</Label>
						{(header.format || '').toLowerCase() === 'text' ? (
							<div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{header.text}</div>
						) : mediaUrl(header) ? (
							<img src={mediaUrl(header)} alt="" style={{ maxWidth: '100%', maxHeight: 180, marginTop: 8, borderRadius: 4 }} />
						) : (
							<i style={{ color: '#999' }}>medya</i>
						)}
					</Segment>
				)}
				{body && (
					<Segment>
						<Label ribbon>BODY</Label>
						<div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{body.text}</div>
					</Segment>
				)}
				{footer && (
					<Segment>
						<Label ribbon>FOOTER</Label>
						<div style={{ marginTop: 8 }}>{footer.text}</div>
					</Segment>
				)}
				{buttons && buttons.buttons && (
					<Segment>
						<Label ribbon>BUTTONS</Label>
						<div style={{ marginTop: 8 }}>
							{buttons.buttons.map((b, i) => (
								<Label key={i} style={{ margin: 2 }} color="blue" basic>
									{b.type}: {b.text || b.url || b.phone_number || b.example || ''}
								</Label>
							))}
						</div>
					</Segment>
				)}
				{carousel && carousel.cards && (
					<Segment>
						<Label ribbon>CAROUSEL · {carousel.cards.length} kart</Label>
						<div style={{ display: 'flex', gap: 10, overflowX: 'auto', marginTop: 8 }}>
							{carousel.cards.map((card, ci) => {
								const ch = _.find(card.components, (c) => (c.type || '').toLowerCase() === 'header');
								const cb = _.find(card.components, (c) => (c.type || '').toLowerCase() === 'body');
								const cbtn = _.find(card.components, (c) => (c.type || '').toLowerCase() === 'buttons');
								const url = mediaUrl(ch);
								const isProduct = ch && (ch.format || '').toLowerCase() === 'product';
								return (
									<div key={ci} style={{ minWidth: 160, maxWidth: 160, border: '1px solid #eee', borderRadius: 6, padding: 8 }}>
										<div style={{ fontSize: 11, color: '#888' }}>Kart {ci + 1}</div>
										{isProduct ? (
											<div style={{ textAlign: 'center', color: '#888', padding: 10 }}>
												<Icon name="box" size="big" />
											</div>
										) : url ? (
											<img src={url} alt="" style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 4 }} />
										) : null}
										{cb && <div style={{ fontSize: 12, marginTop: 6 }}>{cb.text}</div>}
										{cbtn && cbtn.buttons && cbtn.buttons.map((b, bi) => (
											<div key={bi} style={{ fontSize: 11, color: '#00a5f4', marginTop: 4 }}>{b.text || b.type}</div>
										))}
									</div>
								);
							})}
						</div>
					</Segment>
				)}
			</div>
		);
	}

	return (
		<Page
			header={
				<Breadcrumb size="big">
					<Breadcrumb.Section as={Link} to={`/settings`}>
						<Icon name="setting" />
						{t('settings')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section as={Link} to={`/settings/integrations`}>
						{t('integrations')}
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section as={Link} to={`/settings/integrations/360dialog`}>
						360Dialog
					</Breadcrumb.Section>
					<Breadcrumb.Divider icon="right chevron" />
					<Breadcrumb.Section>{t('templates') || 'Şablonlar'}</Breadcrumb.Section>
				</Breadcrumb>
			}
			extra={
				<div className="ml-auto">
					<Button primary disabled={!waba_gen_id} onClick={() => setModalOpen(true)}>
						<Icon name="plus" />
						{t('addTemplate') || 'Şablon Ekle'}
					</Button>
				</div>
			}
			flex
		>
			<MyMenu activeItem="">
				<Page.Wrap>
					<Segment>
						{shop && (
							<Message info size="small">
								<Icon name="whatsapp" color="green" /> {shop}
							</Message>
						)}
						{error && (
							<Message negative onDismiss={() => setError('')}>
								<p>{error}</p>
							</Message>
						)}

						<div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
							<Input icon="search" placeholder={t('searchByName') || 'İsimle ara'} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 300 }} />
							<Button basic icon="refresh" content={t('refresh') || 'Yenile'} loading={loading} onClick={fetchTemplates} />
						</div>

						{loading ? (
							<div style={{ textAlign: 'center', padding: 30 }}>
								<Icon name="asterisk" loading size="large" />
							</div>
						) : filtered.length === 0 ? (
							<Message info style={{ textAlign: 'center' }}>
								<p>{t('noTemplatesFound') || 'Şablon bulunamadı.'}</p>
							</Message>
						) : (
							<Table celled>
								<Table.Header>
									<Table.Row>
										<Table.HeaderCell>{t('name') || 'İsim'}</Table.HeaderCell>
										<Table.HeaderCell>{t('category') || 'Kategori'}</Table.HeaderCell>
										<Table.HeaderCell>{t('type') || 'Tip'}</Table.HeaderCell>
										<Table.HeaderCell>{t('preview') || 'Önizleme'}</Table.HeaderCell>
										<Table.HeaderCell>{t('language') || 'Dil'}</Table.HeaderCell>
										<Table.HeaderCell>{t('status') || 'Durum'}</Table.HeaderCell>
										<Table.HeaderCell textAlign="right">{t('actions') || 'İşlemler'}</Table.HeaderCell>
									</Table.Row>
								</Table.Header>
								<Table.Body>
									{filtered.map((tpl) => (
										<Table.Row key={`${tpl.name}_${tpl.language || ''}`}>
											<Table.Cell>{tpl.name}</Table.Cell>
											<Table.Cell>{tpl.category}</Table.Cell>
											<Table.Cell>{templateTypeLabel(tpl)}</Table.Cell>
											<Table.Cell style={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bodyText(tpl)}</Table.Cell>
											<Table.Cell>{tpl.language}</Table.Cell>
											<Table.Cell>
												<Label color={statusColor(tpl.status)}>{tpl.status}</Label>
											</Table.Cell>
											<Table.Cell textAlign="right">
												<Button.Group size="small">
													<Button icon="eye" onClick={() => setViewTarget(tpl)} title={t('view') || 'Görüntüle'} />
													<Button icon="trash" onClick={() => setDeleteTarget(tpl)} title={t('delete') || 'Sil'} />
												</Button.Group>
											</Table.Cell>
										</Table.Row>
									))}
								</Table.Body>
							</Table>
						)}

						<CreateDialog360Template modalOpen={modalOpen} onClose={() => setModalOpen(false)} onTemplateCreated={fetchTemplates} waba_gen_id={waba_gen_id} />

						<Modal open={!!deleteTarget} size="tiny" onClose={() => setDeleteTarget(null)}>
							<Modal.Header>{t('confirmDeleteTemplate') || 'Şablonu Sil'}</Modal.Header>
							<Modal.Content>
								<p>
									{t('deleteTemplateWarning') || 'Bu şablon silinecek:'} <b>{deleteTarget && deleteTarget.name}</b>
								</p>
							</Modal.Content>
							<Modal.Actions>
								<Button onClick={() => setDeleteTarget(null)}>{t('cancel') || 'İptal'}</Button>
								<Button negative loading={deleting} onClick={confirmDelete}>
									{t('delete') || 'Sil'}
								</Button>
							</Modal.Actions>
						</Modal>

						<Modal open={!!viewTarget} onClose={() => setViewTarget(null)} size="small">
							<Modal.Header>{viewTarget && viewTarget.name}</Modal.Header>
							<Modal.Content scrolling>{renderTemplateDetail(viewTarget)}</Modal.Content>
							<Modal.Actions>
								<Button onClick={() => setViewTarget(null)}>{t('close') || 'Kapat'}</Button>
							</Modal.Actions>
						</Modal>
					</Segment>
				</Page.Wrap>
			</MyMenu>
		</Page>
	);
}
